import * as anchor from "@project-serum/anchor"
import { PublicKey } from "@solana/web3.js"
import { Constants } from "./constants.nft"
export interface PDAInfo {
  key: anchor.web3.PublicKey
  bump?: number | null
}

export class PDA {
  readonly programId: anchor.web3.PublicKey
  readonly tokenMetadataProgram: PublicKey

  public constructor(programId?: anchor.web3.PublicKey, tokenMetadataProgramId?: PublicKey) {
    this.programId = programId || Constants.VOUCHER_NFT_PROGRAM_ID
    this.tokenMetadataProgram = tokenMetadataProgramId || Constants.SOLANA_TOKEN_METADATA_PROGRAM
  }

  config = (): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.CONFIG_SEED)],
      this.programId,
    )
    return {
      key: pda,
      bump: bump,
    }
  }

  authority = (): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.AUTHORITY_SEED)],
      this.programId,
    )
    return {
      key: pda,
      bump: bump,
    }
  }

  vault = (seed: string): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.VAULT_SEED), Buffer.from(seed)],
      this.programId,
    )
    return {
      key: pda,
      bump,
    }
  }

  campaign = (campaignId: anchor.BN): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.CAMPAIGN_SEED), campaignId.toArrayLike(Buffer, "le", 8)],
      this.programId,
    )
    return {
      key: pda,
      bump,
    }
  }

  eligibleUsers = (user: PublicKey, campaignId: anchor.BN): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.ELIGIBLE_USERS_SEED), user.toBuffer(), campaignId.toArrayLike(Buffer, "le", 8)],
      this.programId,
    )
    return {
      key: pda,
      bump,
    }
  }

  repayDebtVoucher = (mint: PublicKey): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.REPAY_DEBT_VOUCHER_SEED), mint.toBuffer()],
      this.programId,
    )
    return {
      key: pda,
      bump,
    }
  }

  borrowFeeVoucher = (mint: PublicKey): PDAInfo => {
    const [pda, bump] = anchor.web3.PublicKey.findProgramAddressSync(
      [Buffer.from(Constants.BORROW_FEE_VOUCHER), mint.toBuffer()],
      this.programId,
    )
    return {
      key: pda,
      bump,
    }
  }

  metadata = async (mint: anchor.web3.PublicKey): Promise<PDAInfo> => {
    const [key, bump] = PublicKey.findProgramAddressSync(
      [Buffer.from("metadata"), this.tokenMetadataProgram.toBuffer(), mint.toBuffer()],
      this.tokenMetadataProgram,
    )
    return {
      key,
      bump,
    }
  }

  masterEdition = async (mint: anchor.web3.PublicKey): Promise<PDAInfo> => {
    const [key, bump] = PublicKey.findProgramAddressSync(
      [Buffer.from("metadata"), this.tokenMetadataProgram.toBuffer(), mint.toBuffer(), Buffer.from("edition")],
      this.tokenMetadataProgram,
    )
    return {
      key,
      bump,
    }
  }

  obligationLending = async (
    user: PublicKey,
    lendingMarketAddress: string,
    lendingProgramId: PublicKey,
  ): Promise<PDAInfo> => {
    return {
      key: await PublicKey.createWithSeed(user, lendingMarketAddress.slice(0, 32), lendingProgramId),
    }
  }
}
